<template>
  <div class="flex align-items-center whitespace-nowrap">
    {{ I18n.t("image.rate_this_result") }}

    <IconButton class="ml-3" variant="secondary" :disabled="isRated" :title="I18n.t('image.rate_good')" @click="ratePositive"><ThumbsUpIcon /></IconButton>

    <template v-if="needsDialog && !isRated">
      <Dialog ref="dialog" :dismissable="false" trigger-classes="w-full flex justify-center sm:justify-start focus:outline-none" dialog-classes="md:!max-w-4xl">
        <template #trigger>
          <IconButton variant="secondary" :title="I18n.t('image.rate_bad')"><ThumbsDownIcon /></IconButton>
        </template>
        <template v-slot:content="{ closeDialog }">
          <div class="flex flex-col gap-y-6">
            <Heading level="h4">{{ I18n.t("improve.heading") }}</Heading>
            <p>{{ I18n.t("improve.want_to_help") }}</p>
            <ul style="list-style-type: disc; list-style-position: inside">
              <li>{{ I18n.t("improve.contribute_and_improve") }}</li>
              <li>{{ I18n.t("improve.teach_ai") }}</li>
              <li>{{ I18n.t("improve.get_better") }}</li>
            </ul>

            <p>
              <Checkbox :v-model="consented" :error="hasConsentError" @update:modelValue="handleConsentChange"><span v-html="I18n.t('improve.agree_to_x', { x: ipc_url })"></span></Checkbox>
              <Checkbox :v-model="rememberDecision" @update:modelValue="(newValue) => (rememberDecision = newValue)">{{ I18n.t("improve.remember") }}</Checkbox>
            </p>

            <div class="sm:flex sm:flex-row-reverse sm:gap-x-3 space-y-3 sm:space-y-0 grow">
              <Button
                fullWidth
                class="mb-2"
                autofocus
                type="button"
                :on-click="
                  () => {
                    contribute(closeDialog);
                  }
                "
                >{{ I18n.t("improve.yes_contribute") }}</Button
              >
              <Button
                fullWidth
                variant="secondary"
                type="reset"
                :on-click="
                  () => {
                    onlyRate(closeDialog);
                  }
                "
                >{{ I18n.t("improve.no_contribute") }}</Button
              >
            </div>
          </div>
        </template>
      </Dialog>
    </template>
    <template v-else>
      <IconButton variant="secondary" :disabled="isRated" :title="I18n.t('image.rate_bad')" @click="rateNegative"><ThumbsDownIcon /></IconButton>
    </template>
  </div>
</template>

<script setup lang="ts">
import "@/src/i18n";
import { IconButton, ThumbsUpIcon, ThumbsDownIcon, Dialog, Heading, Button, Checkbox } from "prism";
import improvedecision from "@/src/improvedecision";
import { onMounted, ref, watch, inject, computed } from "vue";
import Routes from "@/modules/routes";
import { Image, ProcessingState } from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import { rbgRateBadV100, rbgRateGoodV100 } from "kaleido-event-tracker";
import { useEditorStore } from "@/stores/editor_store";

const I18n = inject("I18n");

interface RatingProps {
  image: Image;
}

const props = defineProps<RatingProps>();

const consented = ref<Boolean>(false);
const hasConsentError = ref<Boolean>(false);
const needsDialog = ref<Boolean>(false);
const rememberDecision = ref<Boolean>(false);

const isRated = ref<Boolean>(false);
const rating = ref<Number>(0);
const store = useEditorStore();

onMounted(() => {
  const decision = improvedecision.get();

  if (decision === undefined) {
    needsDialog.value = true;
    consented.value = false;
  } else if (decision === "accept") {
    consented.value = true;
    needsDialog.value = false;
  } else if (decision === "reject") {
    consented.value = false;
    needsDialog.value = false;
  }

  refresh();
});

watch(
  () => props.image,
  () => {
    refresh();
  }
);

const refresh = () => {
  if (props.image.previewResult.state === ProcessingState.Finished) {
    isRated.value = props.image.previewResult.rated;
    rating.value = props.image.previewResult.rating;
  } else {
    isRated.value = false;
    rating.value = 0;
  }
};

const handleConsentChange = (newValue) => {
  hasConsentError.value = false;
  consented.value = newValue;
};

const contribute = (closeDialog) => {
  if (!consented.value) {
    hasConsentError.value = true;
    return;
  }
  saveDecision();
  rateNegative();
  closeDialog();
};

const onlyRate = (closeDialog) => {
  saveDecision();
  rateNegative();
  closeDialog();
};

const saveDecision = () => {
  if (rememberDecision.value) {
    improvedecision.set(consented.value ? "accept" : "reject");
    needsDialog.value = false;
  }
};

const ratePositive = () => {
  window.track("Images", "rate_good", "Rate good");
  rbgRateGoodV100({ image_id: props.image.meta.id });
  rate(1);
};

const rateNegative = () => {
  window.track("Images", "rate_bad", "Rate bad");
  rbgRateBadV100({ image_id: props.image.meta.id });
  rate(-1);
};

const rate = (newRating: number) => {
  Client.rateImage(props.image, newRating, consented.value);
  isRated.value = true;
  rating.value = newRating;

  const image = props.image;

  if (image.previewResult.state == ProcessingState.Finished) {
    image.previewResult.rated = isRated.value as boolean;
    image.previewResult.rating = rating.value as number;
    store.updateImage(image);
  }
};

const ipc_url = computed(() => {
  return `<a href="${Routes.get("ipc_url")}" target="_blank">${I18n.t("improve.ipc")}</a>`;
});
</script>
